import {
	ArrowRight,
	BookOpenCheck,
	ClipboardList,
	Database,
	ListChecks,
	Search,
	SlidersHorizontal,
	Sparkles,
} from "lucide-react";
import { useMemo, useState } from "react";
import type React from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	evidenceLevelNotes,
	getPracticeEvidenceSources,
	getQuestionEvidenceSources,
	getQuestionReferenceRecords,
	modeTheoryNotes,
} from "@/data/evidenceCatalog";
import {
	domains,
	type DomainId,
	practiceCatalog,
	type Practice,
	type PracticeId,
	type Question,
	questions,
	situations,
	type Situation,
	type SituationId,
} from "@/pages/AssessmentPlayground";
import { cn } from "@/lib/utils";

const domainEntries = Object.entries(domains) as Array<[DomainId, (typeof domains)[DomainId]]>;
const situationEntries = Object.values(situations);
const practiceEntries = Object.entries(practiceCatalog) as Array<[PracticeId, Practice]>;

export const TrainingConfig: React.FC = () => {
	const [activeDomain, setActiveDomain] = useState<DomainId>("sleep");
	const [activeSituationId, setActiveSituationId] = useState<SituationId>("sleep_onset");
	const [query, setQuery] = useState("");

	const filteredSituationEntries = useMemo(() => {
		const keyword = query.trim().toLowerCase();
		return situationEntries.filter((situation) => {
			if (situation.domain !== activeDomain) {
				return false;
			}
			if (!keyword) {
				return true;
			}
			return [
				situation.name,
				situation.description,
				...situation.practiceIds.map((id) => practiceCatalog[id].title),
			]
				.join(" ")
				.toLowerCase()
				.includes(keyword);
		});
	}, [activeDomain, query]);

	const activeSituation = (filteredSituationEntries.find((item) => item.id === activeSituationId) ??
		filteredSituationEntries[0] ??
		situationEntries.find((item) => item.domain === activeDomain)) as Situation | undefined;

	const relatedQuestions = useMemo(() => {
		if (!activeSituation) {
			return [];
		}
		return questions.filter(
			(question) =>
				question.id === "entry" ||
				question.id === "general-impact" ||
				question.targetDomains?.includes(activeDomain) ||
				questionTouchesSituation(question, activeSituation.id),
		);
	}, [activeDomain, activeSituation]);

	const situationPracticeEntries = useMemo(() => {
		if (!activeSituation) {
			return [];
		}
		return activeSituation.practiceIds.map((id) => [id, practiceCatalog[id]] as const);
	}, [activeSituation]);

	const counts = {
		domain: domainEntries.length,
		practice: practiceEntries.length,
		question: questions.length,
		situation: situationEntries.length,
	};

	return (
		<div className="space-y-6">
			<section className="rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
				<div className="flex flex-wrap items-start justify-between gap-5">
					<div className="max-w-3xl">
						<div className="flex flex-wrap items-center gap-2">
							<Badge className="bg-primary text-primary-foreground">共享配置</Badge>
							<Badge variant="outline">题库录入 → 训练配置 → 做题体验</Badge>
						</div>
						<h1 className="mt-4 text-3xl font-semibold tracking-tight text-foreground">
							状态识别与练习配置
						</h1>
						<p className="mt-3 text-base leading-7 text-muted-foreground">
							这里不再维护第二套 mock
							数据。当前页面和“做题体验”读取同一套领域、具体情况、题目、练习和证据说明，用来检查每个状态如何被题目识别，又会推荐哪些训练。
						</p>
					</div>
					<div className="grid min-w-64 grid-cols-2 gap-3">
						<MetricCard label="领域" value={counts.domain} />
						<MetricCard label="具体情况" value={counts.situation} />
						<MetricCard label="判断题" value={counts.question} />
						<MetricCard label="练习动作" value={counts.practice} />
					</div>
				</div>
			</section>

			<section className="grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
				<Card className="rounded-2xl">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-lg">
							<Database className="size-5 text-primary" />
							领域
						</CardTitle>
						<CardDescription>先看领域覆盖，再看每个领域下的具体情况。</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="relative">
							<Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								className="pl-9"
								onChange={(event) => setQuery(event.target.value)}
								placeholder="搜情况或练习"
								value={query}
							/>
						</div>
						<div className="grid gap-2">
							{domainEntries.map(([id, domain]) => {
								const count = situationEntries.filter((item) => item.domain === id).length;
								return (
									<button
										className={cn(
											"rounded-xl border px-4 py-3 text-left transition-colors",
											activeDomain === id
												? "border-primary bg-primary text-primary-foreground"
												: "border-border/70 bg-background hover:border-primary/50 hover:bg-muted/60",
										)}
										key={id}
										onClick={() => {
											setActiveDomain(id);
											setActiveSituationId(
												situationEntries.find((item) => item.domain === id)?.id ?? "sleep_onset",
											);
										}}
										type="button"
									>
										<div className="flex items-center justify-between gap-3">
											<span className="font-medium">{domain.name}</span>
											<Badge variant={activeDomain === id ? "secondary" : "outline"}>{count}</Badge>
										</div>
										<p
											className={cn(
												"mt-2 text-xs leading-5",
												activeDomain === id
													? "text-primary-foreground/80"
													: "text-muted-foreground",
											)}
										>
											{domain.description}
										</p>
									</button>
								);
							})}
						</div>
					</CardContent>
				</Card>

				<div className="grid gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
					<Card className="rounded-2xl">
						<CardHeader>
							<CardTitle className="flex items-center gap-2 text-lg">
								<SlidersHorizontal className="size-5 text-primary" />
								具体情况
							</CardTitle>
							<CardDescription>
								点选一种情况，右侧会显示判断题、命中规则和匹配练习。
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="grid gap-3">
								{filteredSituationEntries.map((situation) => (
									<button
										className={cn(
											"rounded-2xl border p-4 text-left transition-colors",
											activeSituation?.id === situation.id
												? "border-primary bg-primary/8"
												: "border-border/70 bg-background hover:border-primary/45 hover:bg-muted/50",
										)}
										key={situation.id}
										onClick={() => setActiveSituationId(situation.id)}
										type="button"
									>
										<div className="flex items-start justify-between gap-3">
											<h2 className="text-base font-semibold text-foreground">{situation.name}</h2>
											<Badge variant="outline">{situation.practiceIds.length} 练习</Badge>
										</div>
										<p className="mt-2 text-sm leading-6 text-muted-foreground">
											{situation.description}
										</p>
										<div className="mt-3 flex flex-wrap gap-2">
											<Badge variant="secondary">{domains[situation.domain].name}</Badge>
											{situation.practiceIds.slice(0, 2).map((practiceId) => (
												<Badge key={practiceId} variant="outline">
													{practiceCatalog[practiceId].title}
												</Badge>
											))}
										</div>
									</button>
								))}
								{filteredSituationEntries.length === 0 ? (
									<div className="rounded-2xl border border-dashed border-border p-5 text-sm text-muted-foreground">
										没有匹配的情况。换一个搜索词试试。
									</div>
								) : null}
							</div>
						</CardContent>
					</Card>

					{activeSituation ? (
						<div className="space-y-5">
							<SituationOverview situation={activeSituation} />
							<QuestionRules questions={relatedQuestions} situation={activeSituation} />
							<PracticeRules practices={situationPracticeEntries} />
						</div>
					) : null}
				</div>
			</section>
		</div>
	);
};

function MetricCard({ label, value }: { label: string; value: number }) {
	return (
		<div className="rounded-xl border border-border/70 bg-background/80 p-4">
			<p className="text-xs text-muted-foreground">{label}</p>
			<p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
		</div>
	);
}

function SituationOverview({ situation }: { situation: Situation }) {
	const domain = domains[situation.domain];
	const sourceSet = new Map(
		situation.practiceIds
			.flatMap((id) => getPracticeEvidenceSources(id, practiceCatalog[id]))
			.map((source) => [source.id, source]),
	);

	return (
		<Card className="rounded-2xl">
			<CardHeader>
				<div className="flex flex-wrap items-center gap-2">
					<Badge>{domain.name}</Badge>
					<Badge variant="outline">{situation.practiceIds.length} 个可配练习</Badge>
				</div>
				<CardTitle className="text-2xl">{situation.name}</CardTitle>
				<CardDescription className="text-base leading-7">{situation.description}</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-4 md:grid-cols-2">
				<InfoBlock
					icon={<ClipboardList className="size-5 text-primary" />}
					title="判断方式"
					value="入口题先分流领域，后续题通过选项给具体情况加分、降分或排除；达到最少题数和分数阈值后生成判断。"
				/>
				<InfoBlock
					icon={<BookOpenCheck className="size-5 text-primary" />}
					title="证据链"
					value={Array.from(sourceSet.values())
						.map((source) => source.title)
						.join(" / ")}
				/>
			</CardContent>
		</Card>
	);
}

function QuestionRules({
	questions: relatedQuestions,
	situation,
}: {
	questions: Question[];
	situation: Situation;
}) {
	return (
		<Card className="rounded-2xl">
			<CardHeader>
				<div className="flex flex-wrap items-center gap-2">
					<ListChecks className="size-5 text-primary" />
					<Badge variant="secondary">{relatedQuestions.length} 题</Badge>
				</div>
				<CardTitle className="text-xl">用哪些题判断</CardTitle>
				<CardDescription>
					这些题来自同一套做题体验配置。每个选项会命中领域或具体情况分数。
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-3">
				{relatedQuestions.map((question, index) => {
					const evidenceSources = getQuestionEvidenceSources(question);
					const referenceRecords = getQuestionReferenceRecords(question);
					return (
						<div
							className="rounded-2xl border border-border/70 bg-background/80 p-4"
							key={question.id}
						>
							<div className="flex flex-wrap items-start justify-between gap-3">
								<div>
									<p className="text-xs font-semibold text-primary">
										Q{index + 1} · {question.type === "multi" ? "多选" : "单选"} · 优先级{" "}
										{question.priority}
									</p>
									<h3 className="mt-2 text-lg font-semibold text-foreground">{question.title}</h3>
									<p className="mt-1 text-sm leading-6 text-muted-foreground">{question.helper}</p>
								</div>
								<Badge
									variant={questionTouchesSituation(question, situation.id) ? "default" : "outline"}
								>
									{questionTouchesSituation(question, situation.id) ? "直接命中" : "领域辅助"}
								</Badge>
							</div>

							<div className="mt-4 grid gap-2">
								{question.options.map((option) => (
									<div
										className="grid gap-2 rounded-xl border border-border/55 bg-muted/30 p-3 md:grid-cols-[1fr_1fr]"
										key={option.id}
									>
										<div>
											<p className="text-sm font-medium text-foreground">{option.label}</p>
											{option.description ? (
												<p className="mt-1 text-xs text-muted-foreground">{option.description}</p>
											) : null}
										</div>
										<div className="flex flex-wrap gap-2">
											{describeOptionSignals(option).map((signal) => (
												<Badge key={signal} variant="outline">
													{signal}
												</Badge>
											))}
										</div>
									</div>
								))}
							</div>

							<div className="mt-4 flex flex-wrap gap-2">
								{evidenceSources.slice(0, 4).map((source) => (
									<a
										className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-background px-3 py-1 text-xs font-medium text-foreground hover:border-primary"
										href={source.url}
										key={source.id}
										rel="noreferrer"
										target="_blank"
									>
										{source.title}
										<ArrowRight className="size-3" />
									</a>
								))}
							</div>

							{referenceRecords.length > 0 ? (
								<div className="mt-4 rounded-xl border border-border/70 bg-primary/5 p-3">
									<div className="flex flex-wrap items-center gap-2">
										<Badge>题库参考记录</Badge>
										<Badge variant="outline">{referenceRecords.length} 条</Badge>
									</div>
									<div className="mt-3 grid gap-2">
										{referenceRecords.map((reference) => (
											<div
												className="rounded-lg border border-border/60 bg-background/80 p-3"
												key={reference.id}
											>
												<div className="flex flex-wrap items-center gap-2">
													<Badge variant="secondary">{reference.title}</Badge>
													<Badge variant="outline">{reference.id}</Badge>
												</div>
												<p className="mt-2 text-sm font-medium text-foreground">
													{reference.questionStem}
												</p>
												<p className="mt-1 text-xs leading-5 text-muted-foreground">
													测：{reference.testedState}；评分口径：{reference.scoringNote}
												</p>
											</div>
										))}
									</div>
								</div>
							) : null}
						</div>
					);
				})}
			</CardContent>
		</Card>
	);
}

function PracticeRules({ practices }: { practices: Array<readonly [PracticeId, Practice]> }) {
	return (
		<Card className="rounded-2xl">
			<CardHeader>
				<div className="flex flex-wrap items-center gap-2">
					<Sparkles className="size-5 text-primary" />
					<Badge variant="secondary">{practices.length} 个</Badge>
				</div>
				<CardTitle className="text-xl">匹配后的练习动作</CardTitle>
				<CardDescription>
					练习不是只按产品感觉放进去，而是要说明理论、推荐理由、边界和替代方案。
				</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-3 lg:grid-cols-2">
				{practices.map(([id, practice], index) => {
					const evidenceSources = getPracticeEvidenceSources(id, practice);
					return (
						<div
							className={cn(
								"rounded-2xl border p-4",
								index === 0
									? "border-primary/35 bg-primary/7"
									: "border-border/70 bg-background/80",
							)}
							key={id}
						>
							<div className="flex flex-wrap gap-2">
								<Badge variant={index === 0 ? "default" : "outline"}>
									{index === 0 ? "默认主练习" : "备选"}
								</Badge>
								<Badge variant="secondary">{practice.mode}</Badge>
								<Badge variant="outline">{practice.category}</Badge>
								<Badge variant="outline">证据：{practice.evidenceLevel}</Badge>
							</div>
							<h3 className="mt-3 text-lg font-semibold text-foreground">{practice.title}</h3>
							<p className="mt-1 text-sm text-muted-foreground">{practice.duration}</p>
							<p className="mt-3 text-sm leading-6 text-muted-foreground">{practice.reason}</p>

							<div className="mt-4 grid gap-3 text-sm">
								<PracticeFact label="练习理论" value={practice.theory} />
								<PracticeFact label="为什么推荐" value={practice.selectionReason} />
								<PracticeFact label="为什么不是别的" value={practice.notOtherReason} />
								<PracticeFact label="更好替代" value={practice.betterAlternative} />
							</div>

							<div className="mt-4 rounded-xl border border-border/60 bg-muted/30 p-3">
								<p className="text-xs font-semibold text-muted-foreground">
									{practice.evidenceLevel} · {evidenceLevelNotes[practice.evidenceLevel]}
								</p>
								<p className="mt-2 text-xs leading-5 text-muted-foreground">
									{modeTheoryNotes[practice.mode].reason}
								</p>
							</div>

							<div className="mt-4 flex flex-wrap gap-2">
								{evidenceSources.map((source) => (
									<a
										className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-background px-3 py-1 text-xs font-medium text-foreground hover:border-primary"
										href={source.url}
										key={source.id}
										rel="noreferrer"
										target="_blank"
									>
										{source.title}
										<ArrowRight className="size-3" />
									</a>
								))}
							</div>
						</div>
					);
				})}
			</CardContent>
		</Card>
	);
}

function PracticeFact({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-xl border border-border/60 bg-background/70 p-3">
			<p className="text-xs font-semibold text-muted-foreground">{label}</p>
			<p className="mt-1 leading-6 text-foreground">{value}</p>
		</div>
	);
}

function InfoBlock({
	icon,
	title,
	value,
}: {
	icon: React.ReactNode;
	title: string;
	value: string;
}) {
	return (
		<div className="rounded-xl border border-border/70 bg-background/80 p-4">
			<div className="flex items-center gap-2">
				{icon}
				<p className="font-semibold text-foreground">{title}</p>
			</div>
			<p className="mt-3 text-sm leading-6 text-muted-foreground">{value || "待补充"}</p>
		</div>
	);
}

function questionTouchesSituation(question: Question, situationId: SituationId) {
	return (
		question.targetSituations?.includes(situationId) ||
		question.options.some((option) => situationId in (option.situationScores ?? {}))
	);
}

function describeOptionSignals(option: Question["options"][number]) {
	const signals: string[] = [];

	for (const [id, score] of Object.entries(option.domainScores ?? {})) {
		signals.push(`${domains[id as DomainId].name} ${formatScore(score)}`);
	}

	for (const [id, score] of Object.entries(option.situationScores ?? {})) {
		signals.push(`${situations[id as SituationId].name} ${formatScore(score)}`);
	}

	for (const id of option.excludeSituations ?? []) {
		signals.push(`排除 ${situations[id].name}`);
	}

	return signals.length > 0 ? signals : ["记录选择，不直接加分"];
}

function formatScore(score: number | undefined) {
	if (!score) {
		return "+0";
	}
	return score > 0 ? `+${score}` : `${score}`;
}
